import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../context/LanguageContext';
import { useCommonTranslations } from '../context/commonTranslations';
import { usePaymentTranslations } from '../context/paymentTranslations';
import {
  deleteItemFromCartAsync,
  selectItems,
  // updateCartAsync,
} from '../features/cart/cartSlice';
import { useForm } from 'react-hook-form';
import { updateUserAsync } from '../features/user/userSlice';
import { useState, useEffect } from 'react';
import PayPalCheckoutButton from '../features/payment/components/PayPalCheckoutButton';
import PaymentMethodModal from '../features/payment/components/PaymentMethodModal';
import {
  selectCurrentOrder,
  selectStatus,
  selectError,
  setPendingOrder,
} from '../features/order/orderSlice';
import { selectUserInfo } from '../features/user/userSlice';
import { Grid } from 'react-loader-spinner';
import {
  UserIcon,
  MapPinIcon,
  CreditCardIcon,
  BanknotesIcon,
  ShoppingBagIcon,
  TrashIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  PlusIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  ExclamationCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../components/MainLayout';

// Helper function to get the correct price based on plan type
function getPriceByPlanType(product, planType = 'monthly') {
  if (planType === 'yearly') {
    return product.yearlyDiscountPrice || product.yearlyPrice || 0;
  }
  return product.discountPrice || product.price || 0;
}

// Helper function to get multilingual text
function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

function Checkout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = useCommonTranslations(language);
  const tp = usePaymentTranslations(language);

  const methodParam = searchParams.get('method'); // 'paypal' or 'manual'

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm();

  const user = useSelector(selectUserInfo);
  const items = useSelector(selectItems);
  const status = useSelector(selectStatus);
  const currentOrder = useSelector(selectCurrentOrder);
  const orderError = useSelector(selectError);

  const totalAmount = items.reduce(
    (amount, item) => getPriceByPlanType(item.product, item.planType) * item.quantity + amount,
    0
  );
  const totalItems = items.reduce((total, item) => item.quantity + total, 0);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    methodParam === 'manual' ? 'manual' : 'paypal'
  );
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(!methodParam);
  const [subscriptionType, setSubscriptionType] = useState('myaccount');
  const [checkoutEmail] = useState('');
  const [expandedPaymentMethod, setExpandedPaymentMethod] = useState(1);
  const [copiedField, setCopiedField] = useState(null);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (methodParam === 'manual' || methodParam === 'paypal') {
      setSelectedPaymentType(methodParam);
    }
  }, [methodParam]);

  const handleSelectMethod = (type) => {
    setSelectedPaymentType(type);
    setSearchParams({ method: type });
    setIsPaymentModalOpen(false);
  };

  const handleRemove = (e, id) => {
    dispatch(deleteItemFromCartAsync(id));
  };

  const handleAddress = (e) => {
    if (user && user.addresses) {
      setSelectedAddress(user.addresses[e.target.value]);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Dynamically resolve customer address / delivery credentials from form or saved address
  const getOrderAddress = () => {
    const values = getValues();
    const name = values.name || selectedAddress?.name || user?.name || '';
    const email = values.email || selectedAddress?.email || user?.email || '';
    const contactNumber = values.contactNumber || selectedAddress?.contactNumber || '';
    const accountUsername = values.accountUsername || selectedAddress?.accountUsername || '';
    const accountPassword = values.accountPassword || selectedAddress?.accountPassword || '';

    return {
      name,
      email,
      contactNumber,
      subscriptionType,
      accountUsername: subscriptionType === 'myaccount' ? accountUsername : null,
      accountPassword: subscriptionType === 'myaccount' ? accountPassword : null,
    };
  };

  // Form validation prior to order creation or PayPal capture
  const validateForm = () => {
    setLocalError(null);
    const values = getValues();
    const name = values.name || selectedAddress?.name || user?.name;
    const email = values.email || selectedAddress?.email || user?.email;
    const contactNumber = values.contactNumber || selectedAddress?.contactNumber;
    const accountUsername = values.accountUsername || selectedAddress?.accountUsername;
    const accountPassword = values.accountPassword || selectedAddress?.accountPassword;

    if (!name || !email || !contactNumber) {
      setLocalError('Please complete your Full Name, Email Address, and Contact Number above.');
      window.scrollTo({ top: 120, behavior: 'smooth' });
      return false;
    }

    if (subscriptionType === 'myaccount' && (!accountUsername || !accountPassword)) {
      setLocalError('Please provide your Account Username and Password above for subscription setup.');
      window.scrollTo({ top: 220, behavior: 'smooth' });
      return false;
    }

    return true;
  };

  const handleOrder = (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    const effectiveAddress = getOrderAddress();
    const orderData = {
      items,
      totalAmount,
      totalItems,
      user: user.id,
      paymentMethod: 'receipt', // Backend expects 'receipt' as string, not numeric ID
      selectedAddress: effectiveAddress,
      subscriptionType,
      checkoutEmail: checkoutEmail || effectiveAddress.email,
    };

    dispatch(setPendingOrder(orderData));
    setTimeout(() => navigate('/receipt-upload'), 100);
  };

  return (
    <MainLayout>
      {!items.length && <Navigate to="/" replace={true}></Navigate>}
      {!user && <Navigate to="/login" replace={true}></Navigate>}
      {currentOrder && currentOrder.id && (
        <Navigate to={`/order-success/${currentOrder.id}`} replace={true}></Navigate>
      )}

      {status === 'loading' ? (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <Grid
            height="80"
            width="80"
            color="#0ea5e9"
            ariaLabel="grid-loading"
            radius="12.5"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
          />
        </div>
      ) : (
        <div className="min-h-screen bg-white py-8 sm:py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Page Header */}
            <div className="mb-6 flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-500 text-white">
                <ShoppingBagIcon className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900">
                  {t.checkout.pageTitle}
                </h1>
                <p className="text-slate-500 text-sm">
                  {t.checkout.pageSubtitle}
                </p>
              </div>
            </div>

            {/* Payment Mode Active Banner */}
            <div className="mb-8 p-4 rounded-2xl border-2 bg-gradient-to-r from-blue-50/70 via-white to-sky-50/70 border-blue-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-sm flex-shrink-0 ${
                    selectedPaymentType === 'paypal'
                      ? 'bg-gradient-to-tr from-blue-600 to-sky-500'
                      : 'bg-slate-800'
                  }`}
                >
                  {selectedPaymentType === 'paypal' ? (
                    <CreditCardIcon className="w-6 h-6" />
                  ) : (
                    <BanknotesIcon className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Payment Mode
                    </span>
                    <span
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 ${
                        selectedPaymentType === 'paypal'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {selectedPaymentType === 'paypal' && <BoltIcon className="w-3 h-3 text-emerald-600" />}
                      {selectedPaymentType === 'paypal' ? 'Instant Activation' : 'Manual Receipt'}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mt-0.5">
                    {selectedPaymentType === 'paypal'
                      ? 'PayPal / Debit or Credit Card'
                      : 'Bank Transfer & Crypto (Revolut, Pakistani Banks, Binance)'}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPaymentModalOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-white hover:bg-blue-50 border border-blue-200 rounded-xl shadow-xs transition-colors self-start sm:self-auto"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                <span>Change Payment Option</span>
              </button>
            </div>

            {/* Error Alert */}
            {(localError || orderError) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-900">{t.checkout.error || 'Error'}</h3>
                  <p className="text-sm text-red-700 mt-1">{localError || orderError}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Left Column - Forms */}
              <div className="lg:col-span-2 space-y-6">
                {/* Personal Information Form */}
                <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-200 bg-blue-50">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
                        <UserIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-lg font-medium text-slate-900">
                          {t.checkout.personalInfo}
                        </h2>
                        <p className="text-sm text-slate-500">
                          {t.checkout.personalInfoDesc}
                        </p>
                      </div>
                    </div>
                  </div>

                  <form
                    className="p-6"
                    noValidate
                    onSubmit={handleSubmit((data) => {
                      const addressData = {
                        name: data.name,
                        email: data.email,
                        contactNumber: data.contactNumber,
                        subscriptionType: subscriptionType,
                        accountUsername: subscriptionType === 'myaccount' ? data.accountUsername : null,
                        accountPassword: subscriptionType === 'myaccount' ? data.accountPassword : null,
                      };
                      dispatch(
                        updateUserAsync({
                          ...user,
                          addresses: [...user.addresses, addressData],
                        })
                      );
                      reset();
                      setSubscriptionType('myaccount');
                    })}
                  >
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-slate-700 mb-2"
                        >
                          {t.checkout.fullName} *
                        </label>
                        <input
                          type="text"
                          {...register('name', {
                            required: t.checkout.fullNameRequired,
                          })}
                          id="name"
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                          placeholder={t.checkout.fullNamePlaceholder}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-slate-700 mb-2"
                        >
                          {t.checkout.emailAddress} *
                        </label>
                        <input
                          id="email"
                          {...register('email', {
                            required: t.checkout.emailRequired,
                          })}
                          type="email"
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                          placeholder={t.checkout.emailPlaceholder}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="contactNumber"
                          className="block text-sm font-medium text-slate-700 mb-2"
                        >
                          {t.checkout.contactNumber} *
                        </label>
                        <input
                          id="contactNumber"
                          {...register('contactNumber', {
                            required: t.checkout.contactRequired,
                          })}
                          type="tel"
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                          placeholder={t.checkout.contactPlaceholder}
                        />
                        {errors.contactNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.contactNumber.message}
                          </p>
                        )}
                      </div>

                      {/* Subscription Type Selection */}
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          {t.checkout.subscriptionType} *
                        </label>
                        <div className="space-y-3">
                          <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            subscriptionType === 'myaccount'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-blue-200 hover:bg-white'
                          }`}>
                            <input
                              type="radio"
                              name="subscriptionType"
                              value="myaccount"
                              checked={subscriptionType === 'myaccount'}
                              onChange={(e) => setSubscriptionType(e.target.value)}
                              className="mt-1 h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {t.checkout.myAccountOption}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {t.checkout.myAccountOptionDesc}
                              </p>
                            </div>
                          </label>

                          <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            subscriptionType === 'provided'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-blue-200 hover:bg-white'
                          }`}>
                            <input
                              type="radio"
                              name="subscriptionType"
                              value="provided"
                              checked={subscriptionType === 'provided'}
                              onChange={(e) => setSubscriptionType(e.target.value)}
                              className="mt-1 h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {t.checkout.providedOption}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {t.checkout.providedOptionDesc}
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Account Credentials - Only show if "Provide subscription on my account" is selected */}
                      {subscriptionType === 'myaccount' && (
                        <>
                          <div>
                            <label
                              htmlFor="accountUsername"
                              className="block text-sm font-medium text-slate-700 mb-2"
                            >
                              {t.checkout.accountUsername} *
                            </label>
                            <input
                              type="text"
                              {...register('accountUsername', {
                                required: subscriptionType === 'myaccount' ? t.checkout.accountUsernameRequired : false,
                              })}
                              id="accountUsername"
                              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                              placeholder={t.checkout.accountUsernamePlaceholder}
                            />
                            {errors.accountUsername && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.accountUsername.message}
                              </p>
                            )}
                          </div>

                          <div>
                            <label
                              htmlFor="accountPassword"
                              className="block text-sm font-medium text-slate-700 mb-2"
                            >
                              {t.checkout.accountPassword} *
                            </label>
                            <input
                              type="password"
                              {...register('accountPassword', {
                                required: subscriptionType === 'myaccount' ? t.checkout.accountPasswordRequired : false,
                              })}
                              id="accountPassword"
                              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                              placeholder={t.checkout.accountPasswordPlaceholder}
                            />
                            {errors.accountPassword && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.accountPassword.message}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-6 flex items-center justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => reset()}
                        className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                      >
                        {t.checkout.reset}
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2.5 px-5 rounded-lg transition-all duration-200 text-sm"
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t.checkout.addAddress}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Saved Addresses - Only show for myaccount subscription type */}
                {subscriptionType === 'myaccount' && (
                <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-200 bg-blue-50">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
                        <MapPinIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-lg font-medium text-slate-900">
                          {t.checkout.deliveryAddress}
                        </h2>
                        <p className="text-sm text-slate-500">
                          {t.checkout.deliveryAddressDesc}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {user && user.addresses && user.addresses.length > 0 ? (
                      <ul className="space-y-3">
                        {user.addresses.map((address, index) => (
                          <li
                            key={index}
                            className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 ${
                              selectedAddress === address
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 hover:border-blue-200 hover:bg-white'
                            }`}
                            onClick={() =>
                              user && user.addresses && setSelectedAddress(user.addresses[index])
                            }
                          >
                            <div className="flex items-start gap-4">
                              <input
                                onChange={handleAddress}
                                name="address"
                                type="radio"
                                value={index}
                                checked={selectedAddress === address}
                                className="mt-1 h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900">
                                  {address.name}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  {address.street}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {address.city} - {address.pinCode}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-slate-600">
                                  {address.phone}
                                </p>
                              </div>
                            </div>
                            {selectedAddress === address && (
                              <div className="absolute top-4 right-4">
                                <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-center text-slate-500 py-8">
                        {t.checkout.noSavedAddresses}
                      </p>
                    )}
                  </div>
                </div>
                )}

                {/* Checkout Email (Optional) */}
                {/* <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-200 bg-purple-50">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 text-purple-600">
                        <EnvelopeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-lg font-medium text-slate-900">
                          Additional Email
                        </h2>
                        <p className="text-sm text-slate-500">
                          (Optional) Send order confirmation to another email
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <input
                      type="email"
                      placeholder="Enter additional email address (optional)"
                      value={checkoutEmail}
                      onChange={(e) => setCheckoutEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-slate-900 placeholder-slate-400"
                    />
                  </div>
                </div> */}

                {/* Payment Section: Manual Transfer or PayPal Card */}
                {selectedPaymentType === 'manual' ? (
                  <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b border-slate-200 bg-blue-50">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
                          <BanknotesIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h2 className="text-lg font-medium text-slate-900">
                            {t.checkout.paymentMethod}
                          </h2>
                          <p className="text-sm text-slate-500">
                            Transfer amount and upload receipt in the next step
                          </p>
                        </div>
                      </div>
                      
                      {/* Video Tutorial Dropdown */}
                      <details className="mt-4 group max-w-2xl mx-auto">
                        <summary className="flex items-center justify-center gap-2 cursor-pointer p-3 bg-white border border-blue-200 rounded-xl text-blue-600 font-medium hover:bg-blue-50 transition-all select-none shadow-sm">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Watch Payment Tutorial</span>
                          <svg className="w-4 h-4 transition-transform duration-300 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <div className="mt-3 rounded-xl overflow-hidden shadow-sm border border-slate-200 bg-slate-50 flex justify-center">
                          <video 
                            src="/Logos/subifyvideo.mp4" 
                            controls 
                            className="w-full block"
                            style={{ maxHeight: '600px', objectFit: 'contain' }}
                            preload="metadata"
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </details>
                    </div>

                    <div className="p-6">
                      <div className="space-y-3">
                        {tp.paymentMethods.map((method) => (
                          <div key={method.id} className="space-y-2">
                            <div
                              className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-200 ${
                                expandedPaymentMethod === method.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 hover:border-blue-200 hover:bg-white'
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900">
                                  {method.title}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {method.subtitle}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedPaymentMethod(
                                    expandedPaymentMethod === method.id ? null : method.id
                                  );
                                }}
                                className="flex-shrink-0 p-1 hover:bg-blue-100 rounded transition-colors"
                              >
                                <ChevronDownIcon
                                  className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${
                                    expandedPaymentMethod === method.id ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Payment Method Details - Expandable */}
                            {expandedPaymentMethod === method.id && (
                              <div className="ml-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                                {method.instructions && method.instructions.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-slate-900 mb-2">
                                      {tp.instructions}
                                    </h4>
                                    <ol className="list-decimal list-inside space-y-1">
                                      {method.instructions.map((instruction, idx) => (
                                        <li
                                          key={idx}
                                          className="text-xs text-slate-600 ml-2"
                                        >
                                          {instruction}
                                        </li>
                                      ))}
                                    </ol>
                                  </div>
                                )}

                                {method.details && method.details.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-slate-900 mb-2">
                                      {tp.bankDetails}
                                    </h4>
                                    <div className="space-y-2">
                                      {method.details.map((detail, idx) => (
                                        <div
                                          key={idx}
                                          className="flex justify-between items-center bg-white p-2 rounded border border-slate-200"
                                        >
                                          <div>
                                            <p className="text-xs text-slate-500">
                                              {detail.label}
                                            </p>
                                            <p className="text-sm font-medium text-slate-900">
                                              {detail.value}
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              copyToClipboard(detail.value, `${method.id}-${detail.label}`)
                                            }
                                            className="p-1 hover:bg-blue-100 rounded transition-colors"
                                            title="Copy to clipboard"
                                          >
                                            {copiedField === `${method.id}-${detail.label}` ? (
                                              <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                            ) : (
                                              <DocumentDuplicateIcon className="w-4 h-4 text-blue-500" />
                                            )}
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50/70 to-sky-50/70">
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
                          <CreditCardIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h2 className="text-lg font-medium text-slate-900">
                            PayPal & Card Checkout
                          </h2>
                          <p className="text-sm text-slate-500">
                            Instant automated subscription activation
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-emerald-900 space-y-1">
                          <p className="font-bold text-sm">No Receipt Upload Required</p>
                          <p className="text-emerald-700">
                            Your payment is captured and verified automatically. Simply click the yellow <strong>PayPal</strong> button or <strong>Debit / Credit Card</strong> button in your Order Summary on the right to complete checkout.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-subtle border border-slate-200 sticky top-8">
                  <div className="p-6 border-b border-slate-200">
                    <h2 className="text-lg font-medium text-slate-900">
                      {t.checkout.orderSummary}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {totalItems} {t.checkout.itemsInCart}
                    </p>
                  </div>

                  {/* Cart Items */}
                  <div className="max-h-80 overflow-y-auto">
                    <ul className="divide-y divide-slate-200">
                      {items.map((item) => (
                        <li key={item.id} className="p-4">
                          <div className="flex gap-3">
                            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                              <img
                                src={item.product.thumbnail}
                                alt={getMultilingualText(item.product.title, language)}
                                className="h-full w-full object-cover object-center"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-slate-900 truncate">
                                {getMultilingualText(item.product.title, language)}
                              </h3>
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-slate-500">
                                  €{getPriceByPlanType(item.product, item.planType)}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {item.planType === 'yearly' ? 'Yearly' : 'Monthly'}
                                </p>
                              </div>
                              <div className="mt-2">
                                <button
                                  onClick={(e) => handleRemove(e, item.id)}
                                  type="button"
                                  className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                  {t.cart.remove}
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Totals */}
                  <div className="p-6 border-t border-slate-200 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t.checkout.subtotal}</span>
                      <span className="font-medium text-slate-900">
                        €{totalAmount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t.checkout.shipping}</span>
                      <span className="text-slate-500">{t.checkout.calculated}</span>
                    </div>
                    <div className="pt-3 border-t border-slate-200 flex justify-between">
                      <span className="text-base font-medium text-slate-900">
                        {t.checkout.total}
                      </span>
                      <span className="text-lg font-medium text-slate-900">
                        €{totalAmount}
                      </span>
                    </div>
                  </div>

                  {/* Order Button */}
                  <div className="p-6 pt-0">
                    {selectedPaymentType === 'manual' ? (
                      <button
                        onClick={handleOrder}
                        type="button"
                        className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase shadow-sm hover:shadow"
                      >
                        {t.checkout.placeOrder}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <PayPalCheckoutButton
                          items={items}
                          totalAmount={totalAmount}
                          totalItems={totalItems}
                          selectedAddress={selectedAddress}
                          subscriptionType={subscriptionType}
                          checkoutEmail={checkoutEmail}
                          onValidate={validateForm}
                          getOrderAddress={getOrderAddress}
                        />
                      </div>
                    )}

                    <div className="mt-4 text-center">
                      <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
                      >
                        {t.checkout.continueShopping}
                        <ArrowRightIcon className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Switcher Modal */}
      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSelect={handleSelectMethod}
      />
    </MainLayout>
  );
}

export default Checkout;
